// ── Three-way (diff3) line merge ────────────────────────────────────────────
// Pure function: no I/O, no platform deps. Unit-testable in isolation and the
// reference implementation for the Swift mirror (`ThreeWayMerge.swift`).
// Change both or neither.
//
// Given a common ancestor (`base`) plus two divergent versions (`ours`,
// `theirs`), auto-merge non-overlapping line changes and flag only the regions
// both sides edited differently. This is the classic diff3 algorithm (the same
// family node-diff3 implements) built on an O(n*m) LCS so it stays easy to
// verify and mirror. Notes are small, so the quadratic LCS is a non-issue.
//
// Frontmatter (reminder YAML) is NEVER line-merged: the document is split into
// its frontmatter block and body, each merged separately, and the frontmatter
// block is merged whole (coarse: unchanged / one-sided / identical / conflict)
// so YAML can't be corrupted by interleaving lines.

import { splitFrontmatterDocument } from "./index"

export type MergeHunkType = "stable" | "ours" | "theirs" | "conflict"

export interface MergeHunk {
  type: MergeHunkType
  /** Lines from the common ancestor for this region. */
  base: string[]
  /** Lines on our side. */
  ours: string[]
  /** Lines on their side. */
  theirs: string[]
  /** True only for the frontmatter block; the resolver hides "Keep both" (two
   *  YAML blocks would corrupt reminders). */
  frontmatter?: boolean
}

export interface MergeResult {
  /** True when no hunk is a conflict (fully auto-merged). */
  clean: boolean
  /** The auto-merged document. Valid only when `clean` is true. */
  merged: string
  /**
   * Ordered regions. The shipping sync flow only reads `clean`/`merged` (the
   * manual resolver is whole-note ours/theirs, not per-hunk). `hunks` +
   * `assembleMerge` are the proven, tested substrate reserved for a future
   * per-hunk resolver UI; they are intentionally not dead-code-eliminated.
   */
  hunks: MergeHunk[]
}

export type MergeChoice = "ours" | "theirs" | "both"

/**
 * Three-way merge of markdown note content. Splits frontmatter from body and
 * merges each separately (frontmatter whole-block, body line-by-line via diff3).
 */
export function threeWayMerge(base: string, ours: string, theirs: string): MergeResult {
  const baseDoc = splitFrontmatterDocument(base)
  const oursDoc = splitFrontmatterDocument(ours)
  const theirsDoc = splitFrontmatterDocument(theirs)

  const hunks: MergeHunk[] = []

  const fmHunk = mergeFrontmatter(baseDoc.frontmatter, oursDoc.frontmatter, theirsDoc.frontmatter)
  if (fmHunk) hunks.push(fmHunk)

  const bodyHunks = mergeLines(
    baseDoc.body.split("\n"),
    oursDoc.body.split("\n"),
    theirsDoc.body.split("\n"),
  )
  for (const h of bodyHunks) hunks.push(h)

  const clean = hunks.every((h) => h.type !== "conflict")
  // When clean there are no conflicts, so choices is irrelevant; when dirty the
  // string is documented as invalid, so falling back to "ours" is harmless.
  const merged = assembleMerge(
    hunks,
    hunks.filter((h) => h.type === "conflict").map<MergeChoice>(() => "ours"),
  )

  return { clean, merged, hunks }
}

/**
 * Rebuild the final document from merged hunks. `choices` is indexed by
 * conflict-hunk order (0-based over hunks where `type === 'conflict'`). Non-
 * conflict hunks contribute their already-resolved content.
 */
export function assembleMerge(hunks: MergeHunk[], choices: MergeChoice[]): string {
  const out: string[] = []
  let conflictIndex = 0
  for (const h of hunks) {
    if (h.type === "conflict") {
      const choice = choices[conflictIndex++] ?? "ours"
      if (choice === "ours") out.push(...h.ours)
      else if (choice === "theirs") out.push(...h.theirs)
      else out.push(...h.ours, ...h.theirs)
    } else if (h.type === "stable") {
      out.push(...h.base)
    } else if (h.type === "ours") {
      out.push(...h.ours)
    } else {
      out.push(...h.theirs)
    }
  }
  return out.join("\n")
}

// ── Frontmatter (whole-block) merge ─────────────────────────────────────────

function wrapFrontmatter(fm: string | null): string[] {
  if (fm === null) return []
  return ["---", ...fm.split("\n"), "---"]
}

function mergeFrontmatter(
  base: string | null,
  ours: string | null,
  theirs: string | null,
): MergeHunk | null {
  const oursChanged = ours !== base
  const theirsChanged = theirs !== base

  if (!oursChanged && !theirsChanged) {
    if (base === null) return null
    return { type: "stable", base: wrapFrontmatter(base), ours: [], theirs: [], frontmatter: true }
  }
  if (oursChanged && !theirsChanged) {
    if (ours === null) return null // ours removed the frontmatter
    return { type: "ours", base: [], ours: wrapFrontmatter(ours), theirs: [], frontmatter: true }
  }
  if (!oursChanged && theirsChanged) {
    if (theirs === null) return null // theirs removed the frontmatter
    return { type: "theirs", base: [], ours: [], theirs: wrapFrontmatter(theirs), frontmatter: true }
  }
  // Both changed.
  if (ours === theirs) {
    if (ours === null) return null
    return { type: "ours", base: [], ours: wrapFrontmatter(ours), theirs: [], frontmatter: true }
  }
  return {
    type: "conflict",
    base: wrapFrontmatter(base),
    ours: wrapFrontmatter(ours),
    theirs: wrapFrontmatter(theirs),
    frontmatter: true,
  }
}

// ── diff3 line merge ────────────────────────────────────────────────────────

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

function mergeLines(base: string[], ours: string[], theirs: string[]): MergeHunk[] {
  const regions = diff3MergeIndices(ours, base, theirs)
  const hunks: MergeHunk[] = []
  for (const r of regions) {
    if (r[0] === -1) {
      // [-1, oursStart, oursLen, baseStart, baseLen, theirsStart, theirsLen]
      const o = ours.slice(r[1], r[1] + r[2])
      const bs = base.slice(r[3], r[3] + r[4])
      const t = theirs.slice(r[5], r[5] + r[6])
      // False conflict: both sides made the identical change → auto-take it.
      if (arraysEqual(o, t)) {
        hunks.push({ type: "ours", base: [], ours: o, theirs: [] })
      } else {
        hunks.push({ type: "conflict", base: bs, ours: o, theirs: t })
      }
      continue
    }
    // [side, start, len] — side 0 ours, 1 base(common), 2 theirs.
    const side = r[0]
    const start = r[1]
    const len = r[2]
    if (side === 1) {
      hunks.push({ type: "stable", base: base.slice(start, start + len), ours: [], theirs: [] })
    } else if (side === 0) {
      hunks.push({ type: "ours", base: [], ours: ours.slice(start, start + len), theirs: [] })
    } else {
      hunks.push({ type: "theirs", base: [], ours: [], theirs: theirs.slice(start, start + len) })
    }
  }
  return hunks
}

/** LCS-matched index pairs between two line arrays (O(n*m) DP). */
function lcsMatches(x: string[], y: string[]): Array<[number, number]> {
  const n = x.length
  const m = y.length
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }
  const matches: Array<[number, number]> = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (x[i] === y[j]) {
      matches.push([i, j])
      i++
      j++
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i++
    } else {
      j++
    }
  }
  return matches
}

/** Changed regions between `base` and `other`, in base coordinates. */
function diffIndices(base: string[], other: string[]): Array<{ base: [number, number]; other: [number, number] }> {
  const matches = lcsMatches(base, other)
  const result: Array<{ base: [number, number]; other: [number, number] }> = []
  let bi = 0
  let oi = 0
  for (const [mb, mo] of matches) {
    if (mb > bi || mo > oi) {
      result.push({ base: [bi, mb - bi], other: [oi, mo - oi] })
    }
    bi = mb + 1
    oi = mo + 1
  }
  if (bi < base.length || oi < other.length) {
    result.push({ base: [bi, base.length - bi], other: [oi, other.length - oi] })
  }
  return result
}

/**
 * Core diff3. Returns a flat region list:
 *  - [side, start, len]          side 0=ours, 1=base(common), 2=theirs
 *  - [-1, oL, oLen, bL, bLen, tL, tLen]   a conflict spanning all three
 * Mirrors node-diff3's `diff3MergeIndices`.
 */
function diff3MergeIndices(ours: string[], base: string[], theirs: string[]): number[][] {
  const m1 = diffIndices(base, ours)
  const m2 = diffIndices(base, theirs)

  // hunk = [baseStart, side, baseLen, otherStart, otherLen]
  const hunks: number[][] = []
  for (const h of m1) hunks.push([h.base[0], 0, h.base[1], h.other[0], h.other[1]])
  for (const h of m2) hunks.push([h.base[0], 2, h.base[1], h.other[0], h.other[1]])
  hunks.sort((a, b) => a[0] - b[0] || a[1] - b[1])

  const result: number[][] = []
  let commonOffset = 0
  const copyCommon = (target: number) => {
    if (target > commonOffset) {
      result.push([1, commonOffset, target - commonOffset])
      commonOffset = target
    }
  }

  for (let i = 0; i < hunks.length; i++) {
    const first = i
    const regionLhs = hunks[i][0]
    let regionRhs = regionLhs + hunks[i][2]
    // Grow the region to swallow every overlapping hunk (from either side).
    while (i < hunks.length - 1) {
      const next = hunks[i + 1]
      if (next[0] > regionRhs) break
      regionRhs = Math.max(regionRhs, next[0] + next[2])
      i++
    }

    copyCommon(regionLhs)

    if (first === i) {
      // Exactly one hunk → a one-sided change, no conflict.
      if (hunks[i][4] > 0) result.push([hunks[i][1], hunks[i][3], hunks[i][4]])
    } else {
      // ≥2 overlapping hunks → real conflict. For each side, track the changed
      // sub-range in that side's coords (r[0..1]) and in base coords (r[2..3]).
      const reg: Record<number, number[]> = {
        0: [ours.length, -1, base.length, -1],
        2: [theirs.length, -1, base.length, -1],
      }
      for (let k = first; k <= i; k++) {
        const side = hunks[k][1]
        const r = reg[side]
        const oL = hunks[k][0]
        const oR = oL + hunks[k][2]
        const abL = hunks[k][3]
        const abR = abL + hunks[k][4]
        r[0] = Math.min(abL, r[0])
        r[1] = Math.max(abR, r[1])
        r[2] = Math.min(oL, r[2])
        r[3] = Math.max(oR, r[3])
      }
      // Extend each side's extent to cover the FULL base region [regionLhs,
      // regionRhs): lines inside the region that a side left unchanged map 1:1
      // into that side, so shift the changed extent by the surrounding gap.
      const oursL = reg[0][0] + (regionLhs - reg[0][2])
      const oursR = reg[0][1] + (regionRhs - reg[0][3])
      const theirsL = reg[2][0] + (regionLhs - reg[2][2])
      const theirsR = reg[2][1] + (regionRhs - reg[2][3])
      result.push([-1, oursL, oursR - oursL, regionLhs, regionRhs - regionLhs, theirsL, theirsR - theirsL])
    }

    commonOffset = regionRhs
  }

  copyCommon(base.length)
  return result
}
